/**
 * The attendance register — `/countries/:cc/field/attendance` (v17 F4).
 *
 * ===========================================================================
 * WHAT A ROW IS, AND WHAT IT IS NOT
 * ===========================================================================
 * One row = one engineer's clock cycle on one country-local day, as the
 * SERVER recorded it: `clock_in_at`/`clock_out_at` are server receipts, the
 * work date was computed in the country's timezone on the server, and the
 * position stamps are what the DEVICE REPORTED at those moments — claims,
 * stored beside receipts, worded as claims (the anti-spec inversion: nothing
 * on this screen asserts where a person WAS). A forgotten clock-out renders
 * as the warning it is (`missing_clock_out`) — the server never fabricates a
 * closing time and this register does not either: the OUT cell says "Not
 * clocked out", not an invented five o'clock.
 *
 * Position cells follow Rule 14 three-ways (see `bits.tsx`): the caller's
 * payload already carries the per-row masking decision, and the screen
 * renders what arrived rather than re-guessing it from permissions.
 *
 * THE DAY TIMELINE IS ONE CLICK FROM EVERY ROW. The row carries exactly the
 * two things the timeline route needs (`user_id`, `work_date`), which is why
 * the register — not a directory the viewer may not hold — is the way in.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { formatDateLong } from '../inquiries/format';
import { fetchAttendanceRegister, fieldErrorText, fieldKeys } from './api';
import type { AttendanceFilters, AttendanceRow } from './api';
import { DateRangeFilter, EngineerFilterRow, PositionCell, TimeCell, engineerOptionsOf } from './bits';
/*
 * The renewals pager is the EXTRACTED one (its header records why: the
 * honest-count rule lives once). Reused rather than copied for that reason.
 */
import { PagerFooter } from '../renewals/PagerFooter';

const PER_PAGE = 50;

export function AttendanceRegisterPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'field.attendance.view');

  const userId = params.get('user_id');
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);

  const filters = useMemo<AttendanceFilters>(
    () => ({
      userId: userId ?? undefined,
      dateFrom: from === '' ? undefined : from,
      dateTo: to === '' ? undefined : to,
      page,
      perPage: PER_PAGE,
    }),
    [userId, from, to, page],
  );

  const list = useQuery({
    queryKey: fieldKeys.attendance(countryCode, filters),
    queryFn: ({ signal }) => fetchAttendanceRegister(countryCode, filters, signal),
    enabled: canView,
  });

  if (!canView) {
    // D5 — a designed refusal naming who to ask, not a blank screen and not a
    // table rendered empty (which would read as "nobody clocked in").
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="Attendance is not available to this account">
          Viewing the attendance register in {countryCode} needs the field attendance
          permission. Ask an administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const rows = list.data?.data ?? [];
  const total = list.data?.total ?? 0;
  const anyFilter = userId !== null || from !== '' || to !== '';

  const patchParams = (mutate: (patch: URLSearchParams) => void): void => {
    const patch = new URLSearchParams(params);
    mutate(patch);
    // A filter change resets paging — page 3 of one engineer's days is not
    // page 3 of the whole register.
    patch.delete('page');
    setParams(patch, { replace: true });
  };

  const setEngineer = (next: string | null): void => {
    patchParams((patch) => {
      if (next === null) patch.delete('user_id');
      else patch.set('user_id', next);
    });
  };

  const setRange = (next: { from: string; to: string }): void => {
    patchParams((patch) => {
      if (next.from === '') patch.delete('from');
      else patch.set('from', next.from);
      if (next.to === '') patch.delete('to');
      else patch.set('to', next.to);
    });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Attendance"
        subtitle={
          list.data === undefined
            ? undefined
            : `${String(total)} ${total === 1 ? 'day recorded' : 'days recorded'} in ${countryCode}`
        }
      />

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <DateRangeFilter from={from} to={to} onChange={setRange} />
        <EngineerFilterRow options={engineerOptionsOf(rows)} active={userId} onSelect={setEngineer} />
      </div>

      {list.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the attendance register" />
        </div>
      ) : list.isError ? (
        <Alert tone="danger" title="The attendance register could not be loaded">
          {/* The server's own sentence — a 400 names the parameter, a 409 the toggle. */}
          <p>{fieldErrorText(list.error)}</p>
          <button
            type="button"
            onClick={() => {
              void list.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : rows.length === 0 && total > 0 ? (
        // An empty PAGE is not an empty RESULT SET — the quotes-list V3 lesson.
        <Alert tone="info" title={`Page ${String(list.data.page)} is past the end of this register`}>
          <p>
            The register holds {String(total)} {total === 1 ? 'day' : 'days'}.
          </p>
          <button
            type="button"
            onClick={() => goToPage(1)}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Show the first page.
          </button>
        </Alert>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          {anyFilter ? (
            <EmptyState
              title="No attendance matches these filters."
              action={
                <button
                  type="button"
                  onClick={() =>
                    patchParams((patch) => {
                      patch.delete('user_id');
                      patch.delete('from');
                      patch.delete('to');
                    })
                  }
                  className="text-body13 font-medium text-indigo hover:underline"
                >
                  Clear the filters.
                </button>
              }
            />
          ) : (
            <EmptyState title="No attendance has been recorded in this country yet." />
          )}
        </div>
      ) : (
        <AttendanceTable countryCode={countryCode} rows={rows} onEngineer={setEngineer} />
      )}

      {list.data !== undefined && rows.length > 0 ? (
        <PagerFooter
          page={list.data.page}
          perPage={list.data.perPage}
          shown={rows.length}
          total={list.data.total}
          onPage={goToPage}
        />
      ) : null}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────── table ── */

const HEADERS = ['ENGINEER', 'CLOCK IN', 'IN POSITION', 'CLOCK OUT', 'OUT POSITION'] as const;

function AttendanceTable({
  countryCode,
  rows,
  onEngineer,
}: {
  countryCode: string;
  rows: readonly AttendanceRow[];
  onEngineer: (userId: string) => void;
}): React.JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[1080px] border-collapse text-left">
        <caption className="sr-only">Attendance recorded in {countryCode}</caption>
        <colgroup>
          <col className="w-[180px]" />
          <col className="w-[130px]" />
          <col className="w-[100px]" />
          <col />
          <col className="w-[130px]" />
          <col />
          <col className="w-[120px]" />
        </colgroup>

        <thead>
          <tr className="h-10 border-b border-line bg-subtle">
            {/* Server-ordered, newest day first — stated, not a dead sort control. */}
            <th scope="col" className="px-3 text-th uppercase text-fg-muted">
              DATE <span aria-hidden="true">↓</span>
              <span className="sr-only">, newest first</span>
            </th>
            {HEADERS.map((header) => (
              <th key={header} scope="col" className="px-3 text-th uppercase text-fg-muted">
                {header}
              </th>
            ))}
            <th scope="col" className="px-3">
              <span className="sr-only">Row actions</span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="crf-register-row border-b border-line last:border-b-0 hover:bg-subtle"
            >
              <td className="px-3 py-3 align-middle text-body13 text-fg">
                {formatDateLong(`${row.work_date}T12:00:00Z`)}
              </td>

              <td className="px-3 py-3 align-middle">
                <button
                  type="button"
                  onClick={() => onEngineer(row.user_id)}
                  title="Filter the register to this engineer"
                  className="text-body13 font-medium text-fg underline-offset-2 hover:underline"
                >
                  {row.user_name ?? `Engineer #${row.user_id}`}
                </button>
              </td>

              <td className="px-3 py-3 align-middle">
                <TimeCell iso={row.clock_in_at} />
              </td>

              <td className="px-3 py-3 align-middle">
                <PositionCell value={row.clock_in_position} />
              </td>

              <td className="px-3 py-3 align-middle">
                {row.clock_out_at !== null ? (
                  <TimeCell iso={row.clock_out_at} />
                ) : row.missing_clock_out ? (
                  /*
                   * The forgotten clock-out — a real state the server refuses to
                   * paper over (no auto-close, ever), addressed by data attribute
                   * so a test does not retarget if the order changes.
                   */
                  <span
                    data-attendance-state="missing-clock-out"
                    className="inline-flex items-center whitespace-nowrap rounded-pill bg-warn-bg px-2.5 py-1 text-label font-medium text-warn-fg"
                  >
                    Not clocked out
                  </span>
                ) : (
                  <span
                    data-attendance-state="on-duty"
                    className="inline-flex items-center whitespace-nowrap rounded-pill bg-info-bg px-2.5 py-1 text-label font-medium text-info-fg"
                  >
                    On duty
                  </span>
                )}
              </td>

              <td className="px-3 py-3 align-middle">
                <PositionCell value={row.clock_out_position} />
              </td>

              <td className="px-3 py-3 align-middle">
                <Link
                  to={`/countries/${countryCode}/field/timeline?user_id=${row.user_id}&date=${row.work_date}`}
                  state={{ engineerName: row.user_name }}
                  className="text-body13 font-medium text-fg underline underline-offset-2 hover:decoration-2"
                >
                  Day timeline
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
